import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, Search, X } from "lucide-react";
import AppLiveClock from "../components/ui/AppLiveClock";
import LeagueFilterRail from "../components/p2p/create/LeagueFilterRail";
import MatchRow from "../components/p2p/create/MatchRow";
import P2PCreateModal from "../components/p2p/create/P2PCreateModal";
import { fetchP2PCreateLeagues } from "../utils/p2pCreateApi";
import {
  addP2PBet,
  initializeP2PShowcaseListeners,
} from "../../redux/slices/p2pShowcaseSlice";
import { selectAppUser } from "../redux/appAuthSlice";
import { syncBondChatForUser } from "../redux/bondchat/generalChatSlice";
import {
  canShowP2PPostOfferOnFixture,
  filterP2PCreateEligibleFixtures,
  flattenLeaguesToFixtures,
  getLeagueId,
  norm,
  rowMatchesQuery,
  searchTokensFromQuery,
} from "../utils/p2pCreateHelpers";
import { sortLeaguesByPriority } from "../utils/leaguePriority";
import { createP2PBetWithAck } from "../redux/p2pSocket";
import "../p2p-create.css";
import "../p2p.css";

const P2PCreate = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const location = useLocation();
  const navigate = useNavigate();
  const [leaguesRaw, setLeaguesRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [modalTarget, setModalTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const inputRef = useRef(null);
  const searchWrapRef = useRef(null);

  const hasQuery = Boolean(String(search || "").trim());
  const isSearchExpanded = searchOpen || hasQuery;

  const openSearch = useCallback(() => setSearchOpen(true), []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearch("");
  }, []);

  const toggleSearch = useCallback(() => {
    if (isSearchExpanded && !hasQuery) {
      setSearchOpen(false);
      return;
    }
    if (isSearchExpanded && hasQuery) {
      closeSearch();
      return;
    }
    openSearch();
  }, [closeSearch, hasQuery, isSearchExpanded, openSearch]);

  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus();
  }, [searchOpen]);

  useEffect(() => {
    if (!isSearchExpanded) return undefined;

    const handlePointerDown = (event) => {
      if (searchWrapRef.current?.contains(event.target)) return;
      if (!hasQuery) setSearchOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (hasQuery) setSearch("");
      else setSearchOpen(false);
      inputRef.current?.blur();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSearchExpanded, hasQuery]);

  const handleSearchBlur = () => {
    window.setTimeout(() => {
      if (
        !String(search || "").trim() &&
        !searchWrapRef.current?.contains(document.activeElement)
      ) {
        setSearchOpen(false);
      }
    }, 120);
  };

  useEffect(() => {
    if (user?._id) {
      dispatch(syncBondChatForUser(user._id));
      initializeP2PShowcaseListeners(dispatch);
    }
  }, [user?._id, dispatch]);

  useEffect(() => {
    if (!toast || toast.type === "pending") return undefined;
    const t = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const incoming = location.state?.fixture;
    if (!incoming) return;
    if (canShowP2PPostOfferOnFixture(incoming)) {
      setModalTarget({
        fixture: incoming,
        league: location.state?.league || null,
      });
    } else {
      setToast({
        type: "error",
        message:
          "P2P creation is closed for this match — it has started, finished, or is within 10 minutes of kickoff.",
      });
    }
    navigate("/app/p2p/create", { replace: true, state: null });
  }, [location.state, navigate]);

  const openChallenge = ({ fixture, league }) => {
    if (!canShowP2PPostOfferOnFixture(fixture)) {
      setToast({
        type: "error",
        message:
          "P2P creation is closed for this match — it has started, finished, or is within 10 minutes of kickoff.",
      });
      return;
    }
    setModalTarget({ fixture, league });
  };

  const closeChallenge = () => setModalTarget(null);

  const handleChallengeSubmit = useCallback(async (payload) => {
    setToast({ type: "pending", message: "Creating challenge…" });
    try {
      const response = await createP2PBetWithAck(payload);
      if (response?.p2pBet?._id) {
        dispatch(addP2PBet(response.p2pBet));
      }
      setToast({
        type: "success",
        message: response?.message || "P2P challenge created successfully.",
      });
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Could not create challenge.",
      });
    }
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchP2PCreateLeagues(10);
        if (!cancelled) setLeaguesRaw(data);
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load leagues");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const allRows = useMemo(
    () => flattenLeaguesToFixtures(leaguesRaw, Date.now(), { p2pCreateEligibleOnly: true }),
    [leaguesRaw]
  );

  const leagueOptions = useMemo(
    () =>
      sortLeaguesByPriority(
        leaguesRaw
          .map((league) => ({
            id: getLeagueId(league),
            apiId: league?.id ?? null,
            name: league?.leagueName || "League",
            country: league?.countryName || "",
            logo: league?.leagueLogo || null,
            count: filterP2PCreateEligibleFixtures(league?.fixtures || []).length,
          }))
          .filter((league) => league.count > 0)
      ),
    [leaguesRaw]
  );

  const activeLeague = useMemo(
    () => leagueOptions.find((l) => l.id === selectedLeagueId) || null,
    [leagueOptions, selectedLeagueId]
  );

  const tokens = useMemo(() => searchTokensFromQuery(search), [search]);

  const visibleRows = useMemo(() => {
    let rows = allRows;
    if (selectedLeagueId) {
      rows = rows.filter((r) => r.league?.id === selectedLeagueId);
    }
    if (tokens.length) {
      rows = rows.filter((r) => rowMatchesQuery(r, tokens));
    }
    return rows;
  }, [allRows, selectedLeagueId, tokens]);

  const isSearching = norm(search).length > 0;

  return (
    <div className="pcr-page">
      <header ref={searchWrapRef} className="pcr-header">
        <div className="pcr-header-row">
          <div className="pcr-header-lead">
            <h1 className="pcr-header-title">Create P2P</h1>
          </div>
          <div className="pcr-header-clock-wrap app-chrome-clock-center">
            <AppLiveClock className="app-chrome-clock" />
          </div>
          <div className="pcr-header-actions">
            <button
              type="button"
              className={`pcr-chrome-search-toggle${
                isSearchExpanded ? " pcr-chrome-search-toggle-on" : ""
              }`}
              onClick={toggleSearch}
              aria-label={isSearchExpanded ? "Close search" : "Search teams"}
              aria-expanded={isSearchExpanded}
            >
              {isSearchExpanded ? (
                <X size={14} strokeWidth={2.3} aria-hidden />
              ) : (
                <Search size={14} strokeWidth={2.3} aria-hidden />
              )}
            </button>
          </div>
        </div>
        {isSearchExpanded ? (
          <div className="pcr-search-panel">
            <Search size={14} aria-hidden className="pcr-chrome-search-icon" />
            <input
              ref={inputRef}
              type="search"
              className="pcr-chrome-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={handleSearchBlur}
              placeholder="Search teams…"
              aria-label="Search teams"
            />
            {hasQuery ? (
              <button
                type="button"
                className="pcr-chrome-search-clear"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            ) : null}
          </div>
        ) : null}
      </header>

      {toast ? (
        <div
          className={`p2p-toast p2p-toast-${toast.type || "success"}`}
          role="status"
          aria-live="polite"
        >
          {toast.type === "pending" ? (
            <Loader2 size={16} className="pcr-spin" aria-hidden />
          ) : null}
          {toast.message}
        </div>
      ) : null}

      <div className="pcr-split">
        {!error ? (
          <LeagueFilterRail
            leagues={loading ? [] : leagueOptions}
            selectedId={selectedLeagueId}
            onSelect={setSelectedLeagueId}
            totalMatches={loading ? 0 : allRows.length}
            loading={loading}
          />
        ) : null}

        <main className="pcr-games">
          <div className="pcr-games-head">
            <h2 className="pcr-games-title">
              {activeLeague ? activeLeague.name : "All matches"}
            </h2>
            <span className="pcr-games-count">
              {visibleRows.length} match{visibleRows.length === 1 ? "" : "es"}
            </span>
          </div>

          {loading ? (
            <div className="pcr-state">
              <Loader2 size={22} className="pcr-spin" />
              <p>Loading leagues &amp; matches…</p>
            </div>
          ) : null}

          {!loading && error ? (
            <div className="pcr-state pcr-state-err">
              <p>{error}</p>
            </div>
          ) : null}

          {!loading && !error && visibleRows.length === 0 ? (
            <div className="pcr-state">
              <p className="pcr-empty-title">
                {isSearching ? "No teams match your search" : "No upcoming matches"}
              </p>
              <p className="pcr-empty-sub">
                {isSearching
                  ? "Try another team name or clear the search."
                  : selectedLeagueId
                  ? "This league has no fixtures right now. Try another league."
                  : "Check back soon. New fixtures are added regularly."}
              </p>
            </div>
          ) : null}

          {!loading && !error && visibleRows.length > 0 ? (
            <div className="pcr-games-list">
              {visibleRows.map((row) => (
                <MatchRow
                  key={row.fixture._id}
                  row={row}
                  hideLeague={Boolean(selectedLeagueId)}
                  onChallenge={openChallenge}
                />
              ))}
            </div>
          ) : null}
        </main>
      </div>

      <P2PCreateModal
        open={Boolean(modalTarget)}
        fixture={modalTarget?.fixture}
        league={modalTarget?.league}
        onClose={closeChallenge}
        onSubmit={handleChallengeSubmit}
      />
    </div>
  );
};

export default P2PCreate;
